import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchScriptResponse, updateScriptResponse } from 'services/api/script.api.service';
import { setLoading, setNotification } from 'state/app.state';
import { LoadingStates, ScriptResponse, ScriptResponseSlim, ThunkAPI } from 'typings';
import { apiErrorNotification } from 'utils/functions';

interface OnOffScript {
  isOnScript: boolean;
  onScriptStep: string | null;
}

interface ScriptState {
  entity: ScriptResponse | null;
  loadingState: LoadingStates;
  isOnScript: boolean;
  onScriptStep: string | null;
  hasQualifyAnswer: boolean;
  // the lead status derived from script response
  scriptResponseLeadStatus: string;
  // the unqualified reason derived from script response
  scriptResponseUnqualifiedReason: string;
  scriptResponseSummaryString: string | null;
}

interface UpdateScriptPayload {
  data: Partial<ScriptResponseSlim>;
}

/**
 * Asynchronous Actions (thunks)
 */
export const apiFetchScriptResponse = createAsyncThunk(
  'script/apiFetchScriptResponse',
  async (leadId: string, thunkApi: ThunkAPI) => {
    try {
      return await fetchScriptResponse(leadId);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const apiUpdateScriptResponse = createAsyncThunk(
  'script/apiUpdateScriptResponse',
  async (payload: UpdateScriptPayload, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      const scriptResponseId: string = thunkApi.getState().script.entity?.id;
      return await updateScriptResponse(scriptResponseId, payload);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

const buildSummaryString = (script: ScriptResponse) => {
  let summaryStr = '';
  const summaryList = script.summary?.list;

  if (summaryList) {
    let questionAnswerList: string[] = [];

    for (const item of summaryList) {
      if (item.question && item.answer) {
        questionAnswerList.push(`${item.question}: ${item.answer}`);
      }
    }

    if (questionAnswerList) {
      summaryStr = questionAnswerList.join('\n');
    }
  }

  return summaryStr;
};

const hasQualifyAnswer = (scriptResponse: ScriptResponse) => {
  const answers = scriptResponse.answers;

  if (!answers) return false;

  return Object.values(answers).some((answer: any) => ['qualify', 'Qualify *'].includes(answer));
};

/* Script State Slice */
const scriptInitialState: ScriptState = {
  entity: null,
  loadingState: LoadingStates.NOT_LOADED,
  isOnScript: false,
  onScriptStep: null,
  hasQualifyAnswer: false,
  scriptResponseLeadStatus: '',
  scriptResponseUnqualifiedReason: '',
  scriptResponseSummaryString: null,
};

export const scriptSlice = createSlice({
  name: 'script',
  initialState: scriptInitialState,
  reducers: {
    // this action explicitly sets the boolean for whether a qualify answer has been chosen
    setHasQualifyAnswer: (state, action: PayloadAction<boolean>) => {
      state.hasQualifyAnswer = action.payload;
    },
    // this action sets the lead's status based on a script response answer
    setScriptResponseLeadStatus: (state, action: PayloadAction<string>) => {
      state.scriptResponseLeadStatus = action.payload;
    },
    // this action sets the lead's unqualified reason based on a script response answer
    setScriptResponseUnqualifiedReason: (state, action: PayloadAction<string>) => {
      state.scriptResponseUnqualifiedReason = action.payload;
    },
    setIsOnScript: (state, action: PayloadAction<OnOffScript>) => {
      state.isOnScript = action.payload.isOnScript;
      state.onScriptStep = action.payload.onScriptStep;
    },
  },
  extraReducers: {
    [apiFetchScriptResponse.pending.toString()]: (state) => {
      state.loadingState = LoadingStates.LOADING;
    },
    [apiFetchScriptResponse.rejected.toString()]: (state) => {
      state.loadingState = LoadingStates.NOT_LOADED;
    },
    [apiFetchScriptResponse.fulfilled.toString()]: (state, action) => {
      state.entity = action.payload;
      state.hasQualifyAnswer = hasQualifyAnswer(action.payload);
      state.scriptResponseSummaryString = buildSummaryString(action.payload);
      state.loadingState = LoadingStates.LOADED;
    },
    [apiUpdateScriptResponse.fulfilled.toString()]: (state, action) => {
      state.entity = { ...state.entity, ...action.payload };
      state.hasQualifyAnswer = hasQualifyAnswer(action.payload);
      state.scriptResponseSummaryString = buildSummaryString(action.payload);
    },
  },
});

export const {
  setHasQualifyAnswer,
  setScriptResponseLeadStatus,
  setScriptResponseUnqualifiedReason,
  setIsOnScript,
} = scriptSlice.actions;

export default scriptSlice.reducer;
